import React, { useState, useEffect, useRef } from 'react'
import styled from 'styled-components'
import { Button } from 'ui/bend'
import CommentAuthor from './CommentAuthor'

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: none;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: 24px;

  @media (max-width: 768px) {
    flex-direction: column;
  }
`

const CommentText = styled.textarea`
  outline: none;
  width: ${(props) => (props.isEdit ? '65%' : '100%')};
  margin-top: ${(props) => (props.isEdit ? '12px' : '0px')};
  resize: none;
  height: ${(props) => (props.isEdit ? '68px' : '48px')};
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.352px;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 12px;
  border: 1px solid #a3a3a3;
  @media (max-width: 768px) {
    width: 100%;
  }
`

const CommentBtn = styled(Button)({
  fontFamily: 'Proxima-Nova',
  fontSize: '16px',
  fontStyle: 'normal',
  fontWeight: '500',
  lineHeight: '24px',
  letterSpacing: '0.8px',
  textTransform: 'uppercase',
  borderRadius: '25px',
  padding: '8px 24px',
  border: '1px solid #000',
  color: (props) => (props.disabled ? '#4c4c4c' : '#fff'),
  background: (props) => (props.disabled ? '#ebebeb' : '#000')
})

const CancelBtn = styled.button`
  color: #000;
  background: #fff;
  font-family: 'Proxima-Nova';
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 24px;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  border-radius: 25px;
  padding: 8px 24px;
  border: 1px solid #a3a3a3;
  margin-left: 24px;
`

const ReplyContainerBox = styled.div`
  display: ${(props) => (props.isReply ? 'flex' : 'block')};
  gap: 12px;
`

const ReplyTextBox = styled.div`
  width: ${(props) => (props.isReply ? '65%' : '100%')};
  @media (max-width: 768px) {
    width: 100%;
  }
`

const ButtonWrapper = styled.div`
  margin-top: 12px;
  margin-left: ${(props) => (props.isReply ? '40px' : '0')};
  display: flex;
`

// Reusable Editor Component
const CommentEditor = ({
  mode,
  initialValue = '',
  onSubmit,
  onCancel,
  comment,
  user
}) => {
  const [text, setText] = useState(initialValue)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const textareaRef = useRef(null)

  useEffect(() => {
    if (textareaRef.current && (mode === 'edit' || mode === 'reply')) {
      textareaRef.current.focus()
    }
  }, [mode])

  const handleSubmit = async () => {
    if (text.trim()) {
      setIsSubmitting(true)
      try {
        await onSubmit(text.trim())
        setText('')
      } catch (err) {
        console.error('Failed to submit:', err)
      } finally {
        setIsSubmitting(false)
      }
    }
  }

  const actionLabel = {
    new: 'Comment',
    edit: 'Comment',
    reply: 'Reply'
  }[mode]

  return (
    <Container>
      <ReplyContainerBox isReply={mode == 'reply' ? true : false}>
        {comment && (
          <CommentAuthor
            user={mode == 'reply' ? user : comment?.user}
            comment={comment}
            mode={mode}
          />
        )}
        <ReplyTextBox isReply={mode == 'reply' ? true : false}>
          <CommentText
            ref={textareaRef}
            placeholder={mode == 'reply' ? 'Add a Reply ' : 'Add a Comment'}
            onChange={(e) => setText(e.target.value)}
            value={text}
            isEdit={mode == 'edit' ? true : false}
          />
        </ReplyTextBox>
      </ReplyContainerBox>
      <ButtonWrapper isReply={mode == 'reply' ? true : false}>
        <CommentBtn
          disabled={!text.length}
          onClick={() => {
            handleSubmit()
          }}
          processing={isSubmitting}
        >
          {actionLabel}
        </CommentBtn>
        <CancelBtn onClick={onCancel ? onCancel : () => setText('')}>
          Cancel
        </CancelBtn>
      </ButtonWrapper>
    </Container>
  )
}

export default CommentEditor
